import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSystem } from '@ohif/core';
import { Icons } from '../Icons';
import classnames from 'classnames';

/**
 * Mobile-only navigation arrows that are always visible as a fixed overlay
 * regardless of the SidePanel state
 */
export const MobileNavigationOverlay: React.FC = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [currentDisplaySetIndex, setCurrentDisplaySetIndex] = useState(0);
  const [totalDisplaySets, setTotalDisplaySets] = useState(0);
  const [currentDisplaySetUID, setCurrentDisplaySetUID] = useState<string | null>(null);
  const [availableDisplaySets, setAvailableDisplaySets] = useState<any[]>([]);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  const { servicesManager, commandsManager, extensionManager } = useSystem();

  // Create portal container on mount
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const container = document.body;
    setPortalContainer(container);

    return () => {
      setPortalContainer(null);
    };
  }, []);

  // Check if we're on mobile
  useEffect(() => {
    const mobileQuery = '(max-width: 768px)';
    const checkMobile = () => {
      setIsMobile(window.matchMedia(mobileQuery).matches);
    };

    checkMobile();
    const mql = window.matchMedia(mobileQuery);

    if ('addEventListener' in mql) {
      mql.addEventListener('change', checkMobile);
      return () => mql.removeEventListener('change', checkMobile);
    } else {
      (mql as any).addListener(checkMobile);
      return () => (mql as any).removeListener(checkMobile);
    }
  }, []);

  // Track current display set and available display sets in the same series
  useEffect(() => {
    if (!isMobile) return;

    const { viewportGridService, displaySetService } = servicesManager.services;

    const updateDisplaySetInfo = () => {
      try {
        const activeViewportId = viewportGridService.getActiveViewportId();
        if (!activeViewportId) {
          setCurrentDisplaySetIndex(0);
          setTotalDisplaySets(0);
          setCurrentDisplaySetUID(null);
          setAvailableDisplaySets([]);
          return;
        }

        // Get display sets for the active viewport
        const viewportDisplaySets =
          viewportGridService.getDisplaySetsUIDsForViewport(activeViewportId);
        const primaryDisplaySetUID = viewportDisplaySets?.[0];

        if (!primaryDisplaySetUID) {
          setCurrentDisplaySetIndex(0);
          setTotalDisplaySets(0);
          setCurrentDisplaySetUID(null);
          setAvailableDisplaySets([]);
          return;
        }

        const primaryDisplaySet = displaySetService.getDisplaySetByUID(primaryDisplaySetUID);
        if (!primaryDisplaySet) return;

        setCurrentDisplaySetUID(primaryDisplaySetUID);

        // Get all display sets in the same series (same SeriesInstanceUID and StudyInstanceUID)
        const allDisplaySets = displaySetService.getActiveDisplaySets();
        const seriesDisplaySets = allDisplaySets.filter(
          (ds: any) =>
            ds.SeriesInstanceUID === primaryDisplaySet.SeriesInstanceUID &&
            ds.StudyInstanceUID === primaryDisplaySet.StudyInstanceUID &&
            ds.SeriesNumber === primaryDisplaySet.SeriesNumber
        );

        // Sort by SeriesNumber or another appropriate property
        seriesDisplaySets.sort((a: any, b: any) => {
          if (a.SeriesNumber !== b.SeriesNumber) {
            return (a.SeriesNumber || 0) - (b.SeriesNumber || 0);
          }
          return 0;
        });

        const currentIndex = seriesDisplaySets.findIndex(
          (ds: any) => ds.displaySetInstanceUID === primaryDisplaySetUID
        );

        setCurrentDisplaySetIndex(currentIndex >= 0 ? currentIndex : 0);
        setTotalDisplaySets(seriesDisplaySets.length);
        setAvailableDisplaySets(seriesDisplaySets);
      } catch (error) {
        console.warn('Error updating display set info:', error);
        setCurrentDisplaySetIndex(0);
        setTotalDisplaySets(0);
        setCurrentDisplaySetUID(null);
        setAvailableDisplaySets([]);
      }
    };

    // Update on viewport change
    const subscription = viewportGridService.subscribe(
      viewportGridService.EVENTS.ACTIVE_VIEWPORT_ID_CHANGED,
      updateDisplaySetInfo
    );

    // Update on display set changes
    const displaySetSubscription = displaySetService.subscribe(
      displaySetService.EVENTS.DISPLAY_SETS_CHANGED,
      updateDisplaySetInfo
    );

    // Initial update
    updateDisplaySetInfo();

    return () => {
      subscription.unsubscribe();
      displaySetSubscription.unsubscribe();
    };
  }, [isMobile, servicesManager]);

  // Navigation handlers using the same logic as double-click thumbnail
  const navigateToDisplaySet = useCallback(
    (displaySetInstanceUID: string) => {
      if (!servicesManager || !commandsManager) return;

      const { hangingProtocolService, viewportGridService, uiNotificationService } =
        servicesManager.services;

      const activeViewportId = viewportGridService.getActiveViewportId();
      const { isHangingProtocolLayout } = viewportGridService.getState();

      let updatedViewports: any[] = [];

      try {
        updatedViewports = hangingProtocolService.getViewportsRequireUpdate(
          activeViewportId,
          displaySetInstanceUID,
          isHangingProtocolLayout
        );
      } catch (error) {
        console.warn('Error getting viewports to update:', error);
        uiNotificationService?.show({
          title: 'Navigation Error',
          message: 'Could not navigate to the selected series.',
          type: 'error',
          duration: 3000,
        });
        return;
      }

      // Use the same command as double-click thumbnail
      commandsManager.run('setDisplaySetsForViewports', {
        viewportsToUpdate: updatedViewports,
      });
    },
    [servicesManager, commandsManager]
  );

  const handleNavigatePrevious = useCallback(() => {
    if (!isMobile || !availableDisplaySets || availableDisplaySets.length === 0) return;
    if (currentDisplaySetIndex <= 0) return;

    const targetDisplaySet = availableDisplaySets[currentDisplaySetIndex - 1];
    if (!targetDisplaySet) return;

    navigateToDisplaySet(targetDisplaySet.displaySetInstanceUID);
  }, [isMobile, currentDisplaySetIndex, availableDisplaySets, navigateToDisplaySet]);

  const handleNavigateNext = useCallback(() => {
    if (!isMobile || !availableDisplaySets || availableDisplaySets.length === 0) return;
    if (currentDisplaySetIndex >= totalDisplaySets - 1) return;

    const targetDisplaySet = availableDisplaySets[currentDisplaySetIndex + 1];
    if (!targetDisplaySet) return;

    navigateToDisplaySet(targetDisplaySet.displaySetInstanceUID);
  }, [
    isMobile,
    currentDisplaySetIndex,
    totalDisplaySets,
    availableDisplaySets,
    navigateToDisplaySet,
  ]);

  // Don't render if not mobile or no display sets or no portal
  if (!isMobile || totalDisplaySets === 0 || totalDisplaySets === 1) {
    return null;
  }
  if (!isMobile || totalDisplaySets === 0 || totalDisplaySets === 1 || !portalContainer) {
    return null;
  }

  const navigationContent = (
    <>
      {/* Previous button */}
      <button
        onClick={handleNavigatePrevious}
        disabled={currentDisplaySetIndex <= 0}
        className={classnames(
          'tt pointer-events-auto fixed left-4 z-[10000] flex h-12 w-12 items-center justify-center rounded-full shadow-lg transition-all',
          currentDisplaySetIndex <= 0
            ? 'cursor-not-allowed bg-gray-700 text-gray-500'
            : 'bg-primary hover:bg-primary-light text-white active:scale-95'
        )}
        title="Previous series"
        aria-label="Previous series"
        aria-disabled={currentDisplaySetIndex <= 0}
        style={{
          bottom: `calc(env(safe-area-inset-bottom, 0px) + 80px)`,
        }}
      >
        <Icons.ArrowLeftBold className="h-8 w-8" />
      </button>

      {/* Display set counter */}
      <div
        className="pointer-events-none fixed left-1/2 z-[10000] -translate-x-1/2 rounded-full bg-black/80 px-3 py-2 text-sm text-white"
        style={{ bottom: `calc(env(safe-area-inset-bottom, 0px) + 16px)` }}
      >
        {currentDisplaySetIndex + 1} / {totalDisplaySets}
      </div>

      {/* Next button */}
      <button
        onClick={handleNavigateNext}
        disabled={currentDisplaySetIndex >= totalDisplaySets - 1}
        className={classnames(
          'pointer-events-auto fixed right-4 z-[10000] flex h-12 w-12 items-center justify-center rounded-full shadow-lg transition-all',
          currentDisplaySetIndex >= totalDisplaySets - 1
            ? 'cursor-not-allowed bg-gray-700 text-gray-500'
            : 'bg-primary hover:bg-primary-light text-white active:scale-95'
        )}
        title="Next series"
        aria-label="Next series"
        aria-disabled={currentDisplaySetIndex >= totalDisplaySets - 1}
        style={{
          bottom: `calc(env(safe-area-inset-bottom, 0px) + 80px)`,
        }}
      >
        <Icons.ArrowRightBold className="h-8 w-8" />
      </button>
    </>
  );

  return createPortal(navigationContent, portalContainer);
};
